import { useState, useEffect } from 'react';

interface AvatarStyles {
  paddingX?: string;
  marginTop?: string;
}

interface InfoStyles {
  dateTextSize?: string;
  doctorNameTextSize?: string;
  marginTop?: string;
}

interface MoreInfoStyles {
  separatorDisplay?: string;
  principalTextSize?: string;
  secondaryTextSize?: string;
  marginTop?: string;
  marginBottom?: string;
}

interface CodeStyles {
  codeTextSize?: string;
}

interface ValidityStyles {
  topDistance?: string;
  codeTextSize?: string;
  validityTextSize?: string;
  boxSize?: string;
}

interface InformationStyles {
  topDistance?: string;
}

interface CardsStyles {
  width?: string;
  medicinesRightDistance?: string;
  examsRightDistance?: string;
  attestationRightDistance?: string;
  referralRightDistance?: string;
  reportRightDistance?: string;
}

const getAvatarStyles = (screenWidth: number): AvatarStyles => {
  if (screenWidth <= 400) {
    return {
      paddingX: '2px',
      marginTop: '2px',
    };
  }
  if (screenWidth < 1000) {
    return {
      paddingX: '4px',
      marginTop: '8px',
    };
  }
  return {};
};

const getInfoStyles = (screenWidth: number): InfoStyles => {
  if (screenWidth <= 344) {
    return {
      dateTextSize: '9px',
      doctorNameTextSize: '10px',
    };
  }
  if (screenWidth <= 360) {
    return { dateTextSize: '10px', doctorNameTextSize: '12px' };
  }
  if (screenWidth <= 400) {
    return { dateTextSize: '10px', doctorNameTextSize: '12px' };
  }
  if (screenWidth <= 450) {
    return { dateTextSize: '10px', doctorNameTextSize: '12px' };
  }
  return { marginTop: '4%' };
};

const getMoreInfoStyles = (screenWidth: number): MoreInfoStyles => {
  if (screenWidth <= 344) {
    return {
      separatorDisplay: 'none',
      principalTextSize: '9px',
      secondaryTextSize: '8px',
    };
  }
  if (screenWidth <= 360) {
    return {
      separatorDisplay: 'none',
      principalTextSize: '9px',
      secondaryTextSize: '9px',
      marginTop: '2%',
    };
  }
  if (screenWidth <= 400) {
    return {
      separatorDisplay: 'none',
      principalTextSize: '10px',
      secondaryTextSize: '9px',
      marginTop: '4%',
    };
  }
  if (screenWidth <= 450) {
    return {
      separatorDisplay: 'none',
      principalTextSize: '12px',
      secondaryTextSize: '11px',
      marginTop: '6%',
    };
  }
  if (screenWidth < 1000) {
    return {
      principalTextSize: '12px',
      secondaryTextSize: '11px',
      marginBottom: '4px',
      marginTop: '4px',
    };
  }
  return { marginTop: '4px', marginBottom: '4px' };
};

const getCodeStyles = (screenWidth: number): CodeStyles => {
  return {};
};

const getValidityStyles = (screenWidth: number): ValidityStyles => {
  if (screenWidth <= 344) {
    return {
      topDistance: '38%',
      codeTextSize: '2px',
      validityTextSize: '5px',
      boxSize: '5%',
    };
  }
  if (screenWidth <= 360) {
    return {
      topDistance: '39%',
      codeTextSize: '2px',
      validityTextSize: '5px',
      boxSize: '27%',
    };
  }
  if (screenWidth <= 400) {
    return { topDistance: '40%', codeTextSize: '6px', validityTextSize: '9px', boxSize: '14%' };
  }
  if (screenWidth <= 450) {
    return { topDistance: '42%', codeTextSize: '7px', validityTextSize: '9px', boxSize: '22%' };
  }
  if (screenWidth < 1000) {
    return { topDistance: '42%', codeTextSize: '8px', validityTextSize: '10px', boxSize: '24%' };
  }
  return { topDistance: '35%', codeTextSize: '9px', validityTextSize: '10px', boxSize: '29%' };
};

const getInformationStyles = (screenWidth: number): InformationStyles => {
  if (screenWidth <= 344) {
    return {
      topDistance: '48%',
    };
  }
  if (screenWidth <= 360) {
    return { topDistance: '48%' };
  }
  if (screenWidth <= 400) {
    return { topDistance: '49%' };
  }
  if (screenWidth <= 450) {
    return { topDistance: '50%' };
  }
  if (screenWidth < 1000) {
    return { topDistance: '51%' };
  }
  return { topDistance: '42%' };
};

const getCardsStyles = (screenWidth: number): CardsStyles => {
  if (screenWidth <= 344) {
    return {
      medicinesRightDistance: '2%',
      examsRightDistance: '6%',
      attestationRightDistance: '5%',
      referralRightDistance: '2%',
      reportRightDistance: '0',
    };
  }
  if (screenWidth <= 360) {
    return {
      medicinesRightDistance: '2%',
      examsRightDistance: '6%',
      attestationRightDistance: '5%',
      referralRightDistance: '2%',
      reportRightDistance: '0',
    };
  }
  if (screenWidth <= 400) {
    return {
      width: '85%',
      medicinesRightDistance: '6%',
      examsRightDistance: '6%',
      attestationRightDistance: '5%',
      referralRightDistance: '4%',
      reportRightDistance: '0',
    };
  }
  if (screenWidth <= 450) {
    return {
      width: '86%',
      medicinesRightDistance: '6%',
      examsRightDistance: '12%',
      attestationRightDistance: '12%',
      referralRightDistance: '6%',
      reportRightDistance: '0',
    };
  }
  if (screenWidth < 1000) {
    return {
      medicinesRightDistance: '6%',
      examsRightDistance: '12%',
      attestationRightDistance: '12%',
      referralRightDistance: '6%',
      reportRightDistance: '0',
    };
  }
  return {};
};

export const usePreviewStyles = () => {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    avatar: getAvatarStyles(screenWidth),
    info: getInfoStyles(screenWidth),
    moreInfo: getMoreInfoStyles(screenWidth),
    code: getCodeStyles(screenWidth),
    validity: getValidityStyles(screenWidth),
    information: getInformationStyles(screenWidth),
    cards: getCardsStyles(screenWidth),
  };
};
